import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Stack,
} from '@mui/material';
import {
    Lock as LockIcon,
    WorkspacePremium as PremiumIcon,
    Login as LoginIcon,
    PersonAddAlt1 as SignUpIcon,
} from '@mui/icons-material';
import { AUTH_ENABLED } from '../authConfig';
import { useTier } from '../useTier';
import RequestAccessForm from './RequestAccessForm';

/**
 * AUTH-7 — action-level tier gate. Handles premium ACTIONS that live on an
 * otherwise-free page (today: Print / PDF on the free Cockpit page, #5). A
 * paid/admin user runs the action directly; a free or anonymous user gets an
 * upgrade prompt instead — never a passcode (the interim passcode wall,
 * PremiumAccess.PremiumButton, was retired at the Phase C cutover — §7c).
 *
 * Flag-bound at module load (like TierGate / useTier). The disabled binding is a
 * defensive plain-button pass-through for a flags-off rollback (see §7c — this is
 * the accepted "free premium, never lockout" trade). Forwards MUI Button props
 * (variant, size, sx, aria-label…).
 */

function UpgradeDialog({ open, onClose, feature = 'this feature', isSignedIn, currentTier = 'free', userEmail = '' }) {
    const [showRequestForm, setShowRequestForm] = useState(false);

    // Reset the reveal state on every close path (backdrop/escape/Close button) so
    // the next open starts from the same "Request access" prompt, not a stale form.
    const handleClose = () => {
        setShowRequestForm(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.9)' }}>
                <PremiumIcon sx={{ color: '#ffd54f', fontSize: 22 }} />
                Premium feature
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: isSignedIn && !showRequestForm ? 1 : (isSignedIn ? 2 : 0) }}>
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, textTransform: 'capitalize' }}>{feature}</Box>{' '}
                    is a premium feature. {isSignedIn
                        ? `Your account is on the ${currentTier} plan — premium access is granted by an administrator.`
                        : 'Sign in to your account to use it.'}
                </Typography>
                {isSignedIn && !showRequestForm && (
                    <Button
                        size="small"
                        onClick={() => setShowRequestForm(true)}
                        sx={{ textTransform: 'none', color: '#90caf9', pl: 0 }}
                    >
                        Request access
                    </Button>
                )}
                {isSignedIn && showRequestForm && (
                    <RequestAccessForm feature={feature} userEmail={userEmail} compact />
                )}
                {!isSignedIn && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 0.5 }}>
                        <Button
                            component={RouterLink}
                            to="/sign-in"
                            variant="contained"
                            startIcon={<LoginIcon />}
                            onClick={handleClose}
                            sx={{ textTransform: 'none' }}
                        >
                            Sign in
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/sign-up"
                            variant="outlined"
                            startIcon={<SignUpIcon />}
                            onClick={handleClose}
                            sx={{ textTransform: 'none', color: '#90caf9', borderColor: 'rgba(144,202,249,0.5)' }}
                        >
                            Create account
                        </Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} size="small" sx={{ color: 'rgba(255,255,255,0.6)' }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function PaidActionEnabled({ onAction, children, feature, startIcon, ...btnProps }) {
    const { isAtLeast, isSignedIn, tier, userEmail } = useTier();
    // TIER-3 Phase 0 (design doc §6.1) refactor: isAtLeast('paid') replaces the
    // old isPaid boolean — identical semantics (this action has one boundary).
    const isPaid = isAtLeast('paid');
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleClick = () => {
        if (isPaid) onAction();
        else setDialogOpen(true);
    };

    return (
        <>
            <Button
                onClick={handleClick}
                startIcon={startIcon !== undefined ? startIcon : (isPaid ? undefined : <LockIcon />)}
                {...btnProps}
            >
                {children}
            </Button>
            <UpgradeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                feature={feature}
                isSignedIn={isSignedIn}
                currentTier={tier}
                userEmail={userEmail}
            />
        </>
    );
}

// AUTH off (rollback): a plain-button pass-through so the action still runs —
// post-§7c-retirement, flags-off means premium actions are open, not locked.
function PaidActionDisabled({ onAction, children, feature, startIcon, ...btnProps }) {
    return (
        <Button onClick={onAction} startIcon={startIcon} {...btnProps}>
            {children}
        </Button>
    );
}

export const PaidAction = AUTH_ENABLED ? PaidActionEnabled : PaidActionDisabled;

export default PaidAction;
